import { useEffect, useMemo, useRef, useState } from "react";
import {
  ActionIcon,
  Alert,
  Anchor,
  Badge,
  Button,
  Container,
  Group,
  Modal,
  Paper,
  ScrollArea,
  Select,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { ResponsiveTable } from "../components/responsiveTable/ResponsiveTable";
import { notifications } from "@mantine/notifications";
import {
  IconCamera,
  IconCameraOff,
  IconCloudDownload,
  IconCloudUpload,
  IconExternalLink,
  IconRefresh,
  IconScan,
  IconTrash,
  IconUpload,
} from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";

import { ApiError, api } from "../api/client";
import type {
  BulkAssignResult,
  DeviceLookupResult,
  ScanSessionSummary,
  Shipment,
} from "../api/types";
import { BarcodeScanner } from "../components/BarcodeScanner";
import {
  AntennaConfirmModal,
  antennaCandidates,
  antennaSkippedMessage,
  cleanDevEui,
} from "../components/AntennaConfirmModal";
import { useCurrentUser } from "../hooks/useCurrentUser";
import { formatTypeCounts } from "../lib/typeCounts";

type Mode = "identify" | "assign" | "bulk" | "lookup";

interface BulkEntry {
  ts: number;
  query: string;
  resolved_dev_eui?: string;
  product_type_slug?: string;
  status: "buffered" | "applied" | "conflict" | "not_found";
  message?: string;
}

interface ScanLog {
  ts: number;
  raw: string;
  result: DeviceLookupResult;
  applied?: { box_number?: string; shipment_id?: number };
  status: "ok" | "warn" | "err" | "applied";
  message: string;
}

const DEDUPE_MS = 2500;
const STORAGE_KEY = "scanner.session.v1";
// Bumped when the meaning of seenKeys changes — older persisted sets are
// discarded at load time so users don't carry over stale dedup entries from a
// buggy build (e.g. bulk-mode used to mark scans seen before the API confirmed
// they existed, blocking re-scans after the underlying device was fixed).
const SEEN_KEYS_SCHEMA = 2;

interface PersistedState {
  mode?: Mode;
  shipmentId?: string | null;
  boxNumber?: string;
  logs?: ScanLog[];
  sessionScanCount?: number;
  seenKeys?: string[];
  seenKeysSchema?: number;
}

function loadPersisted(): PersistedState {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw, stripSecrets) as PersistedState;
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

// Device secrets never go to localStorage: it is readable by any script on
// the origin and survives logout on shared workshop machines.
const SECRET_FIELDS = new Set(["app_key", "join_eui", "nwk_key", "app_s_key", "nwk_s_key"]);

function stripSecrets(_key: string, value: unknown): unknown {
  return SECRET_FIELDS.has(_key) ? undefined : value;
}

export function clearScannerSession(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

function savePersisted(state: PersistedState): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state, stripSecrets));
  } catch {
    // quota or private mode — silently ignore
  }
}

export function Scanner() {
  const { t } = useTranslation();
  const user = useCurrentUser();
  const canEdit = user.role === "admin" || user.role === "operator";
  const navigate = useNavigate();

  const persisted = useRef<PersistedState>(loadPersisted()).current;

  const [mode, setMode] = useState<Mode>(persisted.mode ?? "identify");
  const [cameraOn, setCameraOn] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [manualValue, setManualValue] = useState("");
  const [shipments, setShipments] = useState<Shipment[]>([]);
  const [shipmentId, setShipmentId] = useState<string | null>(
    persisted.shipmentId ?? null,
  );
  const [boxNumber, setBoxNumber] = useState(persisted.boxNumber ?? "");
  const [moveMode, setMoveMode] = useState(false);
  const [fromReturnMode, setFromReturnMode] = useState(false);
  const [logs, setLogs] = useState<ScanLog[]>(persisted.logs ?? []);
  // Total successful scans this session — independent of the (capped) logs table
  // so the camera counter keeps incrementing past 30 entries.
  const [sessionScanCount, setSessionScanCount] = useState<number>(
    persisted.sessionScanCount ?? (persisted.logs ?? []).filter(
      (l) => l.status === "applied" || l.status === "ok",
    ).length,
  );
  // Canonical dedup index — raw scans + resolved dev_euis. Survives the 30-row
  // log cap so re-scans of dropped entries don't bump the counter again.
  // Only successful scans get backfilled from logs so that previously-failed
  // codes can be re-scanned after the underlying device is fixed (e.g. user
  // imports the missing module into a batch).
  const [seenKeys, setSeenKeys] = useState<Set<string>>(() => {
    // Only trust persisted.seenKeys when the schema matches; older versions
    // may include unverified raws from a buggy build of bulk mode.
    const trustPersisted = persisted.seenKeysSchema === SEEN_KEYS_SCHEMA;
    const initial = new Set<string>(trustPersisted ? (persisted.seenKeys ?? []) : []);
    for (const l of persisted.logs ?? []) {
      if (l.status !== "ok" && l.status !== "applied") continue;
      if (l.raw) initial.add(l.raw.trim().toUpperCase());
      const eui = l.result?.device?.dev_eui;
      if (eui) initial.add(eui.toUpperCase());
    }
    return initial;
  });
  const seenKeysRef = useRef(seenKeys);
  useEffect(() => {
    seenKeysRef.current = seenKeys;
  }, [seenKeys]);
  function markSeen(...keys: (string | null | undefined)[]) {
    const toAdd = keys
      .filter((k): k is string => !!k)
      .map((k) => k.trim().toUpperCase())
      .filter(Boolean);
    if (toAdd.length === 0) return;
    for (const k of toAdd) seenKeysRef.current.add(k);
    setSeenKeys((prev) => {
      const next = new Set(prev);
      for (const k of toAdd) next.add(k);
      return next;
    });
  }

  // Saved sessions (server-side)
  const [savedSessions, setSavedSessions] = useState<ScanSessionSummary[]>([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [sessionsOpen, setSessionsOpen] = useState(false);
  const [sessionName, setSessionName] = useState("");
  const [saving, setSaving] = useState(false);

  // DB-side count of devices currently in the selected (shipment, box).
  // Lets the operator spot ghosts left over from a prior session.
  const [boxDbCount, setBoxDbCount] = useState<number | null>(null);
  const [boxDbByType, setBoxDbByType] = useState<Record<string, number> | undefined>(undefined);
  const [flashColor, setFlashColor] = useState<"green" | "yellow" | "red" | null>(null);
  const [annotations, setAnnotations] = useState<Map<string, { color: string; label: string }>>(new Map());
  const flashTimerRef = useRef<number | null>(null);
  const logsRef = useRef<ScanLog[]>([]);
  useEffect(() => {
    logsRef.current = logs;
  }, [logs]);

  function flashCamera(color: "green" | "yellow" | "red") {
    if (flashTimerRef.current) window.clearTimeout(flashTimerRef.current);
    setFlashColor(color);
    flashTimerRef.current = window.setTimeout(() => setFlashColor(null), 800);
  }

  const [bulkEntries, setBulkEntries] = useState<BulkEntry[]>([]);
  const [antennaPrompt, setAntennaPrompt] = useState<{
    ship: number;
    box: string;
    candidates: string[];
  } | null>(null);
  const [antennaCreating, setAntennaCreating] = useState(false);
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [lastBulkResult, setLastBulkResult] = useState<BulkAssignResult | null>(null);
  const bulkEntriesRef = useRef<BulkEntry[]>([]);
  useEffect(() => {
    bulkEntriesRef.current = bulkEntries;
  }, [bulkEntries]);

  const lastScanRef = useRef<{ raw: string; ts: number } | null>(null);
  const manualInputRef = useRef<HTMLInputElement>(null);

  // Use refs so the camera callback (which captures values once) reads fresh state
  const modeRef = useRef(mode);
  const shipmentRef = useRef<string | null>(shipmentId);
  const boxRef = useRef(boxNumber);
  const moveModeRef = useRef(moveMode);
  useEffect(() => {
    moveModeRef.current = moveMode;
  }, [moveMode]);
  const fromReturnModeRef = useRef(fromReturnMode);
  useEffect(() => {
    fromReturnModeRef.current = fromReturnMode;
  }, [fromReturnMode]);
  useEffect(() => {
    modeRef.current = mode;
  }, [mode]);
  useEffect(() => {
    shipmentRef.current = shipmentId;
  }, [shipmentId]);
  useEffect(() => {
    boxRef.current = boxNumber;
  }, [boxNumber]);

  useEffect(() => {
    api.listShipments().then(setShipments).catch(() => undefined);
  }, []);

  useEffect(() => { setAnnotations(new Map()); }, [shipmentId]);

  // Persist scanner state across reloads (mode + shipment + box + logs + counter + seen set).
  useEffect(() => {
    savePersisted({
      mode,
      shipmentId,
      boxNumber,
      logs,
      sessionScanCount,
      seenKeys: Array.from(seenKeys),
      seenKeysSchema: SEEN_KEYS_SCHEMA,
    });
  }, [mode, shipmentId, boxNumber, logs, sessionScanCount, seenKeys]);

  async function refreshBoxDbCount() {
    if (!shipmentId || !boxNumber.trim()) {
      setBoxDbCount(null);
      return;
    }
    try {
      const c = await api.getShipmentBoxCount(Number(shipmentId), boxNumber.trim());
      setBoxDbCount(c.count);
      setBoxDbByType(c.by_type);
    } catch {
      setBoxDbCount(null);
      setBoxDbByType(undefined);
    }
  }

  // Re-fetch whenever shipment or box changes (debounced because boxNumber
  // is a free-text input — wait for the user to stop typing).
  useEffect(() => {
    if (!shipmentId || !boxNumber.trim()) {
      setBoxDbCount(null);
      return;
    }
    const t = window.setTimeout(refreshBoxDbCount, 300);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shipmentId, boxNumber]);

  async function refreshSavedSessions() {
    setSessionsLoading(true);
    try {
      const list = await api.listScanSessions(50);
      setSavedSessions(list);
    } catch {
      // no-op — alerted on first failure
    } finally {
      setSessionsLoading(false);
    }
  }

  function openSaveDialog() {
    const ship = shipments.find((s) => String(s.id) === shipmentId);
    const stamp = new Date().toLocaleString("ro-RO", {
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
    const defaultName = ship && boxNumber.trim()
      ? t("scanner.defaultSessionWithShipBox", { code: ship.code, box: boxNumber.trim(), stamp })
      : ship
        ? t("scanner.defaultSessionWithShip", { code: ship.code, stamp })
        : t("scanner.defaultSessionName", { stamp });
    setSessionName(defaultName);
    setSaveOpen(true);
  }

  async function handleSaveSession() {
    const name = sessionName.trim();
    if (!name) {
      notifications.show({ color: "red", message: t("scanner.nameRequired") });
      return;
    }
    setSaving(true);
    try {
      await api.createScanSession({
        name,
        mode,
        shipment_id: shipmentId ? Number(shipmentId) : null,
        box_number: boxNumber.trim() || null,
        logs,
      });
      notifications.show({
        color: "green",
        message: t("scanner.savedToServer", { count: logs.length }),
      });
      setSaveOpen(false);
      void refreshSavedSessions();
    } catch (err) {
      const detail = err instanceof ApiError ? JSON.stringify(err.detail) : String(err);
      notifications.show({
        color: "red",
        title: t("scanner.saveFailedTitle"),
        message: detail.slice(0, 200),
      });
    } finally {
      setSaving(false);
    }
  }

  async function handleLoadSession(id: number) {
    try {
      const full = await api.getScanSession(id);
      const loadedLogs = (full.data?.logs as ScanLog[] | undefined) ?? [];
      setLogs(loadedLogs);
      setSessionScanCount(
        loadedLogs.filter((l) => l.status === "applied" || l.status === "ok").length,
      );
      if (full.mode === "identify" || full.mode === "assign" || full.mode === "bulk" || full.mode === "lookup") {
        setMode(full.mode);
      }
      setShipmentId(full.shipment_id ? String(full.shipment_id) : null);
      setBoxNumber(full.box_number ?? "");
      setSessionsOpen(false);
      notifications.show({
        color: "blue",
        message: t("scanner.sessionLoaded", { name: full.name, count: loadedLogs.length }),
      });
    } catch (err) {
      const detail = err instanceof ApiError ? JSON.stringify(err.detail) : String(err);
      notifications.show({
        color: "red",
        title: t("scanner.loadFailedTitle"),
        message: detail.slice(0, 200),
      });
    }
  }

  async function handleDeleteSession(id: number) {
    if (!window.confirm(t("scanner.confirmDeleteSession"))) return;
    try {
      await api.deleteScanSession(id);
      setSavedSessions((prev) => prev.filter((s) => s.id !== id));
    } catch (err) {
      const detail = err instanceof ApiError ? JSON.stringify(err.detail) : String(err);
      notifications.show({
        color: "red",
        title: t("scanner.deleteFailedTitle"),
        message: detail.slice(0, 200),
      });
    }
  }

  function startNewSession() {
    setLogs([]);
    setBulkEntries([]);
    setLastBulkResult(null);
    setSessionScanCount(0);
    setSeenKeys(new Set());
    seenKeysRef.current = new Set();
    lastScanRef.current = null;
    notifications.show({
      color: "green",
      message: t("scanner.newSessionToast"),
      autoClose: 1500,
    });
  }

  async function handleScannedText(raw: string) {
    if (!raw) return;
    // dedupe identical rapid scans
    const now = Date.now();
    if (
      lastScanRef.current &&
      lastScanRef.current.raw === raw &&
      now - lastScanRef.current.ts < DEDUPE_MS
    ) {
      return;
    }
    lastScanRef.current = { raw, ts: now };

    const rawKey = raw.trim().toUpperCase();

    // bulk mode: zero round-trip — buffer locally; resolve server-side on save.
    // Dedup against the (un-capped) bulk buffer, NOT against seenKeys, so a
    // user who removes an entry from the buffer can re-scan it after fixing
    // the underlying device (e.g. importing the missing module).
    if (modeRef.current === "bulk") {
      const key = raw.trim();
      if (!key) return;
      const dup = bulkEntriesRef.current.find((b) => b.query.toUpperCase() === rawKey);
      if (dup) {
        flashCamera("yellow");
        notifications.show({
          color: "yellow",
          message: t("scanner.alreadyInList", { key: key.slice(0, 40) }),
          autoClose: 1000,
        });
        return;
      }
      flashCamera("green");
      setBulkEntries((prev) => [
        { ts: now, query: key, status: "buffered" },
        ...prev,
      ]);
      return;
    }

    if (modeRef.current === "lookup") {
      // Navigate to the device-lookup page with the raw code as a query param
      const enc = encodeURIComponent(raw);
      navigate(`/device-lookup?dev_eui=${enc}`);
      return;
    }

    // identify/assign: session-wide dedup against successfully scanned codes.
    if (seenKeysRef.current.has(rawKey)) {
      flashCamera("yellow");
      notifications.show({
        color: "yellow",
        message: t("scanner.alreadyScanned"),
        autoClose: 1000,
      });
      return;
    }

    let result: DeviceLookupResult;
    try {
      result = await api.lookupDevice(raw);
    } catch {
      flashCamera("red");
      addLog({
        ts: now,
        raw,
        result: { found: false, raw_query: raw },
        status: "err",
        message: t("scanner.lookupError"),
      });
      return;
    }

    if (!result.found) {
      flashCamera("red");
      addLog({
        ts: now,
        raw,
        result,
        status: "err",
        message: t("scanner.devEuiUnknownFull", { key: result.dev_eui ?? raw }),
      });
      notifications.show({ color: "red", message: t("scanner.devEuiUnknown"), autoClose: 1500 });
      return;
    }

    const currentMode = modeRef.current;

    if (currentMode === "identify") {
      markSeen(rawKey, result.device?.dev_eui);
      const annoKey = raw.trim().toUpperCase();
      let message: string;
      let status: ScanLog["status"] = "ok";
      let anno: { color: string; label: string } | null = null;
      if (result.return_box) {
        // În cutia de retur — arată și livrarea din care a venit (dacă există).
        flashCamera("yellow");
        status = "warn";
        message = result.shipment
          ? t("scanner.inReturnBoxFromShip", {
              box: result.return_box.code,
              code: result.shipment.code,
              customer: result.shipment.customer ?? "?",
            })
          : t("scanner.inReturnBox", { box: result.return_box.code });
        anno = {
          color: "rgba(232, 140, 40, 1)",
          label: `Retur ${result.return_box.code}${result.shipment ? ` · ${result.shipment.code}` : ""}`,
        };
      } else if (result.shipment) {
        flashCamera("green");
        message = t("scanner.inShipment", { code: result.shipment.code, customer: result.shipment.customer ?? "?" });
        anno = {
          color: "rgba(155, 89, 232, 1)",
          label: `${result.shipment.code}${result.shipment.customer ? ` · ${result.shipment.customer}` : ""}`,
        };
      } else {
        flashCamera("green");
        message = t("scanner.inBatchUnassigned", { code: result.batch?.code ?? "?" });
      }
      if (anno) {
        const a = anno;
        setAnnotations((m) => {
          const n = new Map(m);
          n.set(annoKey, a);
          return n;
        });
      }
      addLog({ ts: now, raw, result, status, message });
      return;
    }

    // assign mode
    const targetShip = shipmentRef.current ? Number(shipmentRef.current) : null;
    const targetBox = boxRef.current.trim();

    if (!targetShip) {
      addLog({
        ts: now,
        raw,
        result,
        status: "err",
        message: t("scanner.selectShipmentTop"),
      });
      return;
    }
    if (!targetBox) {
      addLog({
        ts: now,
        raw,
        result,
        status: "err",
        message: t("scanner.setBoxNumber"),
      });
      return;
    }

    // "From return" mode detaches a return-box module into the target shipment.
    const fromReturn = fromReturnModeRef.current && !!result.return_box;
    if (result.shipment && result.shipment.id !== targetShip && !moveModeRef.current && !fromReturn) {
      flashCamera("yellow");
      const annoKey = raw.trim().toUpperCase();
      const annoLabel = `${result.shipment.code}${result.shipment.customer ? ` · ${result.shipment.customer}` : ""}${result.shipment.index ? ` #${result.shipment.index}` : ""}`;
      setAnnotations((m) => {
        const n = new Map(m);
        n.set(annoKey, { color: "rgba(155, 89, 232, 1)", label: annoLabel });
        return n;
      });
      markSeen(rawKey, result.device?.dev_eui);
      addLog({
        ts: now,
        raw,
        result,
        status: "warn",
        message: t("scanner.conflictMsg", { code: result.shipment.code }),
      });
      notifications.show({
        color: "yellow",
        title: t("scanner.conflictTitle"),
        message: t("scanner.conflictNotice", { code: result.shipment.code }),
        autoClose: 2500,
      });
      return;
    }

    const wasElsewhere = !!(result.shipment && result.shipment.id !== targetShip);
    try {
      const r = await api.markDeviceScanned(result.device!.id, {
        box_number: targetBox,
        shipment_id: targetShip,
        force: moveModeRef.current,
        from_return: fromReturn,
      });
      flashCamera("green");
      markSeen(rawKey, r.device.dev_eui);
      if (fromReturn && result.return_box) {
        const rkey = raw.trim().toUpperCase();
        setAnnotations((m) => {
          const n = new Map(m);
          n.set(rkey, { color: "rgba(232, 140, 40, 1)", label: `Retur ${result.return_box!.code}` });
          return n;
        });
      } else if (wasElsewhere && result.shipment) {
        const movedKey = raw.trim().toUpperCase();
        const movedLabel = `${result.shipment.code}${result.shipment.customer ? ` · ${result.shipment.customer}` : ""}${result.shipment.index ? ` #${result.shipment.index}` : ""}`;
        setAnnotations((m) => {
          const n = new Map(m);
          n.set(movedKey, { color: "rgba(34, 139, 230, 1)", label: movedLabel });
          return n;
        });
      }
      addLog({
        ts: now,
        raw,
        result: { ...result, device: r.device },
        applied: { box_number: targetBox, shipment_id: targetShip },
        status: "applied",
        message: fromReturn
          ? t("scanner.movedFromReturn", { box: result.return_box!.code })
          : wasElsewhere
            ? t("scanner.movedFromShip", { code: result.shipment!.code, box: targetBox })
            : t("scanner.assignedToShipBox", { shipmentId: targetShip, box: targetBox }),
      });
      void refreshBoxDbCount();
    } catch (err) {
      flashCamera("red");
      const detail = err instanceof ApiError ? err.detail : null;
      addLog({
        ts: now,
        raw,
        result,
        status: "err",
        message:
          detail && typeof detail === "object" && "error" in detail
            ? t("scanner.saveFailedDetail", { detail: (detail as Record<string, unknown>).error })
            : t("scanner.saveFailedShort"),
      });
    }
  }

  function addLog(log: ScanLog) {
    setLogs((prev) => [log, ...prev].slice(0, 30));
    if (log.status === "applied" || log.status === "ok") {
      setSessionScanCount((c) => c + 1);
    }
  }

  function removeBulkEntry(ts: number) {
    setBulkEntries((prev) => prev.filter((b) => b.ts !== ts));
  }

  function clearBulkBuffer() {
    setBulkEntries([]);
    setLastBulkResult(null);
  }

  async function submitBulk() {
    const ship = shipmentRef.current ? Number(shipmentRef.current) : null;
    const box = boxRef.current.trim();
    if (!ship) {
      notifications.show({ color: "red", message: t("scanner.selectShipment") });
      return;
    }
    if (!box) {
      notifications.show({ color: "red", message: t("scanner.setBoxNumber") });
      return;
    }
    const buffered = bulkEntriesRef.current.filter((b) => b.status === "buffered");
    if (buffered.length === 0) {
      notifications.show({ color: "yellow", message: t("scanner.noScanInBuffer") });
      return;
    }

    setBulkSubmitting(true);
    try {
      const result = await api.bulkAssign(
        ship,
        box,
        buffered.map((b) => b.query),
      );
      setLastBulkResult(result);

      const appliedMap = new Map(result.applied.map((a) => [a.query, a]));
      const conflictMap = new Map(result.conflicts.map((c) => [c.query, c]));
      const notFoundSet = new Set(result.not_found);

      setBulkEntries((prev) =>
        prev.map((b) => {
          if (b.status !== "buffered") return b;
          const applied = appliedMap.get(b.query);
          if (applied) {
            return {
              ...b,
              status: "applied",
              resolved_dev_eui: applied.dev_eui,
              product_type_slug: applied.product_type_slug,
            };
          }
          const conflict = conflictMap.get(b.query);
          if (conflict) {
            return {
              ...b,
              status: "conflict",
              resolved_dev_eui: conflict.dev_eui,
              message: t("scanner.inOtherShipmentMsg", { code: conflict.in_shipment_code ?? t("scanner.otherShipmentFallback") }),
            };
          }
          if (notFoundSet.has(b.query)) {
            return { ...b, status: "not_found", message: t("scanner.notInDb") };
          }
          return b;
        }),
      );
      notifications.show({
        color: result.applied.length > 0 ? "green" : "yellow",
        title: t("scanner.bulkBoxToastTitle", { box }),
        message: t("scanner.bulkSaveToast", { applied: result.applied.length, conflicts: result.conflicts.length, notFound: result.not_found.length }),
      });
      void refreshBoxDbCount();
      const cands = antennaCandidates(result.not_found);
      if (cands.length > 0) setAntennaPrompt({ ship, box, candidates: cands });
    } catch (err) {
      const detail = err instanceof ApiError ? JSON.stringify(err.detail) : String(err);
      notifications.show({
        color: "red",
        title: t("scanner.bulkSaveErrorTitle"),
        message: detail.slice(0, 200),
      });
    } finally {
      setBulkSubmitting(false);
    }
  }

  async function confirmAntennas(selected: string[]) {
    const prompt = antennaPrompt;
    if (!prompt || selected.length === 0) return;
    setAntennaCreating(true);
    try {
      const r = await api.createAntennas(prompt.ship, selected, prompt.box);
      const appliedMap = new Map(r.applied.map((a) => [cleanDevEui(a.dev_eui), a]));
      const conflictMap = new Map(r.conflicts.map((c) => [cleanDevEui(c.dev_eui), c]));
      setBulkEntries((prev) =>
        prev.map((b) => {
          if (b.status !== "not_found") return b;
          const key = cleanDevEui(b.query);
          const applied = appliedMap.get(key);
          if (applied) {
            return {
              ...b,
              status: "applied",
              resolved_dev_eui: applied.dev_eui,
              product_type_slug: applied.product_type_slug ?? "antenna",
              message: undefined,
            };
          }
          const conflict = conflictMap.get(key);
          if (conflict) {
            return {
              ...b,
              status: "conflict",
              resolved_dev_eui: conflict.dev_eui,
              message: t("scanner.inOtherShipmentMsg", {
                code: conflict.in_shipment_code ?? t("scanner.otherShipmentFallback"),
              }),
            };
          }
          return b;
        }),
      );
      notifications.show({
        color: r.applied.length > 0 ? "green" : "yellow",
        title: t("scanner.bulkBoxToastTitle", { box: prompt.box }),
        message: t("antennaConfirm.done", { count: r.applied.length }),
      });
      const skipped = antennaSkippedMessage(t, r);
      if (skipped) notifications.show({ color: "yellow", message: skipped });
      setAntennaPrompt(null);
      void refreshBoxDbCount();
    } catch (err) {
      const detail = err instanceof ApiError ? JSON.stringify(err.detail) : String(err);
      notifications.show({
        color: "red",
        title: t("scanner.bulkSaveErrorTitle"),
        message: detail.slice(0, 200),
      });
    } finally {
      setAntennaCreating(false);
    }
  }

  function handleManualSubmit() {
    const v = manualValue.trim();
    setManualValue("");
    if (v) void handleScannedText(v);
    manualInputRef.current?.focus();
  }

  const shipmentOptions = useMemo(
    () =>
      shipments.map((s) => ({
        value: String(s.id),
        label: `${s.code} (${formatTypeCounts(t, s.type_counts, s.device_count, `${s.device_count} device`)})`,
      })),
    [shipments],
  );

  // Raw codes already seen this session — passed to the camera overlay so it
  // paints them yellow and skips re-emit. Sourced from the canonical seenKeys
  // set (independent of the 30-row log cap) plus the bulk buffer.
  const knownRawsForCamera = useMemo(() => {
    const s = new Set<string>(seenKeys);
    for (const b of bulkEntries) s.add(b.query.toUpperCase());
    return s;
  }, [seenKeys, bulkEntries]);

  // Counter of new modules in the current session — survives camera on/off,
  // resets only when the user starts a new session. In identify/assign mode
  // we use a standalone counter so it keeps incrementing past the 30-row cap
  // on the logs table.
  const newCount = mode === "bulk" ? bulkEntries.length : sessionScanCount;

  return (
    <Container size="xl">
      <Stack mb="md" gap="sm">
        <Title order={2}>{t("scanner.title")}</Title>
        <Group gap="xs" wrap="wrap">
          <Button
            size="xs"
            variant="light"
            leftSection={<IconCloudUpload size={14} />}
            onClick={openSaveDialog}
            disabled={logs.length === 0}
          >
            {t("scanner.saveOnServer")}
          </Button>
          <Button
            size="xs"
            variant="light"
            leftSection={<IconCloudDownload size={14} />}
            onClick={() => {
              setSessionsOpen(true);
              void refreshSavedSessions();
            }}
          >
            {t("scanner.savedSessions")}
          </Button>
        </Group>
      </Stack>

      <Paper withBorder p="md" mb="md">
        <Group align="flex-end" wrap="wrap" gap="sm">
          <Select
            label={t("scanner.modeLabel")}
            data={[
              { value: "identify", label: t("scanner.modeIdentify") },
              { value: "assign", label: t("scanner.modeAssign") },
              { value: "bulk", label: t("scanner.modeBulk") },
              { value: "lookup", label: t("scanner.modeLookup") },
            ]}
            value={mode}
            onChange={(v) => v && setMode(v as Mode)}
            style={{ flex: "1 1 240px", maxWidth: 320 }}
            allowDeselect={false}
          />
          {(mode === "assign" || mode === "bulk") && (
            <>
              <Select
                label={t("scanner.targetShipmentLabel")}
                placeholder={t("scanner.targetShipmentPlaceholder")}
                data={shipmentOptions}
                value={shipmentId}
                onChange={(v) => {
                  setShipmentId(v);
                  if (v) {
                    const code = shipments.find((s) => String(s.id) === v)?.code;
                    if (code) setBoxNumber(code);
                  }
                }}
                style={{ flex: "1 1 220px", maxWidth: 320 }}
                searchable
                disabled={!canEdit}
              />
              <TextInput
                label={t("scanner.currentBoxLabel")}
                placeholder="2600s"
                value={boxNumber}
                onChange={(e) => setBoxNumber(e.currentTarget.value)}
                style={{ flex: "1 1 140px", maxWidth: 200 }}
                disabled={!canEdit}
              />
              {mode === "assign" && (
                <Switch
                  label={t("scanner.moveMode")}
                  checked={moveMode}
                  onChange={(e) => setMoveMode(e.currentTarget.checked)}
                  color="violet"
                  disabled={!canEdit}
                  style={{ alignSelf: "flex-end" }}
                />
              )}
              {mode === "assign" && (
                <Switch
                  label={t("scanner.fromReturnMode")}
                  checked={fromReturnMode}
                  onChange={(e) => setFromReturnMode(e.currentTarget.checked)}
                  color="orange"
                  disabled={!canEdit}
                  style={{ alignSelf: "flex-end" }}
                />
              )}
              {shipmentId && (
                <Button
                  component={Link}
                  to={`/shipments/${shipmentId}?from=scan`}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="light"
                  leftSection={<IconExternalLink size={14} />}
                  style={{ alignSelf: "flex-end" }}
                >
                  {t("scanner.viewShipment")}
                </Button>
              )}
            </>
          )}
          <Button
            variant={manualOpen ? "filled" : "default"}
            leftSection={<IconScan size={16} />}
            onClick={() => setManualOpen((o) => !o)}
            style={{ alignSelf: "flex-end" }}
          >
            {t("scanner.manualInputLabel")}
          </Button>
          <Group gap="xs" wrap="nowrap" ml="auto" style={{ alignSelf: "flex-end" }}>
            {!cameraOn && (
              <Text size="xs" c="dimmed">{t("scanner.startCameraHint")}</Text>
            )}
            <Switch
              label={cameraOn ? t("scanner.cameraOn") : t("scanner.cameraOff")}
              checked={cameraOn}
              onChange={(e) => setCameraOn(e.currentTarget.checked)}
              thumbIcon={cameraOn ? <IconCamera size={12} /> : <IconCameraOff size={12} />}
            />
          </Group>
        </Group>
      </Paper>

      <Group align="flex-start" gap="md" mb="md" wrap="wrap">
        <Paper
          withBorder
          p="sm"
          style={{
            flex: "1 1 320px",
            maxWidth: 480,
            minHeight: cameraOn ? 320 : 60,
            borderColor: flashColor
              ? `var(--mantine-color-${flashColor}-6)`
              : undefined,
            borderWidth: flashColor ? 4 : undefined,
            transition: "border-color 150ms, border-width 150ms",
          }}
        >
          <div style={{ position: "relative", width: "100%" }}>
            <BarcodeScanner
              active={cameraOn}
              onDetect={(raw) => void handleScannedText(raw)}
              knownRaws={knownRawsForCamera}
              annotations={annotations}
            />
            {cameraOn && (
              <div
                style={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  background: "rgba(0,0,0,0.65)",
                  color: "white",
                  padding: "4px 6px 4px 10px",
                  borderRadius: 8,
                  fontVariantNumeric: "tabular-nums",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
                  <Text component="span" size="xl" fw={700} c="white" lh={1}>
                    {newCount}
                  </Text>
                  <Text component="span" size="xs" c="white" opacity={0.85}>
                    {t("scanner.newCountSuffix")}
                  </Text>
                  {boxDbCount !== null && (
                    <>
                      <Text component="span" size="xs" c="white" opacity={0.6}>
                        /
                      </Text>
                      <Text
                        component="span"
                        size="md"
                        fw={600}
                        c={boxDbCount !== newCount ? "yellow" : "white"}
                        lh={1}
                      >
                        {formatTypeCounts(t, boxDbByType, boxDbCount, String(boxDbCount))}
                      </Text>
                      <Text component="span" size="xs" c="white" opacity={0.85}>
                        {t("scanner.dbCountSuffix")}
                      </Text>
                    </>
                  )}
                </div>
                <ActionIcon
                  size="sm"
                  variant="filled"
                  color="dark"
                  title={t("scanner.newSessionTooltip")}
                  onClick={startNewSession}
                >
                  <IconRefresh size={14} />
                </ActionIcon>
              </div>
            )}
            {flashColor && cameraOn && (
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  pointerEvents: "none",
                  border: `6px solid var(--mantine-color-${flashColor}-6)`,
                  borderRadius: 8,
                  boxShadow: `0 0 24px var(--mantine-color-${flashColor}-6) inset`,
                  animation: "scanner-flash 800ms ease-out",
                }}
              />
            )}
          </div>
        </Paper>

        {manualOpen && (
        <Paper withBorder p="md" style={{ flex: "2 1 280px" }}>
          <Stack>
            <TextInput
              ref={manualInputRef}
              label={t("scanner.manualInputLabel")}
              description={t("scanner.manualInputDesc")}
              placeholder="aabbccddeeff0011"
              size="md"
              value={manualValue}
              onChange={(e) => setManualValue(e.currentTarget.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleManualSubmit();
              }}
              autoFocus
              leftSection={<IconScan size={16} />}
              rightSection={
                <Button size="xs" onClick={handleManualSubmit} disabled={!manualValue.trim()}>
                  {t("scanner.scanButton")}
                </Button>
              }
              rightSectionWidth={70}
            />
            {(mode === "assign" || mode === "bulk") && (!shipmentId || !boxNumber.trim()) && (
              <Alert color="yellow" variant="light">
                {t("scanner.selectBeforeScan")}
              </Alert>
            )}
          </Stack>
        </Paper>
        )}
      </Group>

      {mode === "bulk" && <BulkPanel
        entries={bulkEntries}
        boxNumber={boxNumber}
        submitting={bulkSubmitting}
        canEdit={canEdit}
        lastResult={lastBulkResult}
        onRemove={removeBulkEntry}
        onClear={clearBulkBuffer}
        onSubmit={submitBulk}
      />}

      <AntennaConfirmModal
        opened={antennaPrompt !== null}
        candidates={antennaPrompt?.candidates ?? []}
        loading={antennaCreating}
        onConfirm={(sel) => void confirmAntennas(sel)}
        onCancel={() => setAntennaPrompt(null)}
      />

      {mode !== "bulk" && <Paper withBorder p="md">
        <Group justify="space-between" mb="sm" wrap="wrap" gap="xs">
          <Group gap="xs">
            <Title order={4}>{t("scanner.recentScans")}</Title>
            <Badge variant="light" color="gray">
              {t("scanner.totalBadge", { count: logs.length })}
            </Badge>
            <Badge variant="light" color="green">
              {t("scanner.newBadge", { count: logs.filter((l) => l.status === "applied" || l.status === "ok").length })}
            </Badge>
            <Badge variant="light" color="yellow">
              {t("scanner.warnBadge", { count: logs.filter((l) => l.status === "warn").length })}
            </Badge>
            <Badge variant="light" color="red">
              {t("scanner.errorBadge", { count: logs.filter((l) => l.status === "err").length })}
            </Badge>
          </Group>
          {logs.length > 0 && (
            <Button variant="subtle" size="xs" onClick={startNewSession}>
              {t("scanner.clearAll")}
            </Button>
          )}
        </Group>
        <ResponsiveTable
          stagger={false}
          mobile={{
            rows: logs,
            rowKey: (log) => log.ts + "-" + log.raw,
            title: (log) => (
              <Text ff="monospace" size="sm" span>
                {log.result.device?.dev_eui?.toLowerCase() ?? log.result.dev_eui?.toLowerCase() ?? log.raw.slice(0, 32)}
              </Text>
            ),
            subtitle: (log) => new Date(log.ts).toLocaleTimeString(),
            badge: (log) => <StatusBadge status={log.status} />,
            fields: [
              {
                label: t("scanner.colBatchShipment"),
                hidden: (log) => !log.result.batch && !log.result.shipment,
                render: (log) => (
                  <>
                    {log.result.batch && (
                      <Anchor component={Link} to={`/batches/${log.result.batch.id}`} size="xs">
                        {log.result.batch.code}
                      </Anchor>
                    )}
                    {log.result.shipment && (
                      <>
                        {" "}
                        ·{" "}
                        <Anchor component={Link} to={`/shipments/${log.result.shipment.id}`} size="xs">
                          {log.result.shipment.code}
                        </Anchor>
                      </>
                    )}
                  </>
                ),
              },
              { label: t("scanner.colMessage"), render: (log) => log.message },
            ],
            actions: (log) => (
              <ActionIcon
                variant="light"
                color="red"
                size="lg"
                onClick={() => setLogs((prev) => prev.filter((l) => l.ts !== log.ts || l.raw !== log.raw))}
              >
                <IconTrash size={18} />
              </ActionIcon>
            ),
            empty: t("scanner.noScansYet"),
          }}
        >
        <Table.ScrollContainer minWidth={640}>
        <Table striped withTableBorder>
          <Table.Thead>
            <Table.Tr>
              <Table.Th w={70}>{t("scanner.colStatus")}</Table.Th>
              <Table.Th>DEV_EUI</Table.Th>
              <Table.Th>{t("scanner.colBatchShipment")}</Table.Th>
              <Table.Th>{t("scanner.colMessage")}</Table.Th>
              <Table.Th w={80}>{t("scanner.colTime")}</Table.Th>
              <Table.Th w={40} />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {logs.length === 0 && (
              <Table.Tr>
                <Table.Td colSpan={6}>
                  <Text c="dimmed" size="sm">
                    {t("scanner.noScansYet")}
                  </Text>
                </Table.Td>
              </Table.Tr>
            )}
            {logs.map((log) => (
              <Table.Tr key={log.ts + "-" + log.raw}>
                <Table.Td>
                  <StatusBadge status={log.status} />
                </Table.Td>
                <Table.Td>
                  <Text ff="monospace" size="sm">
                    {log.result.device?.dev_eui?.toLowerCase() ??
                      log.result.dev_eui?.toLowerCase() ??
                      log.raw.slice(0, 32)}
                  </Text>
                </Table.Td>
                <Table.Td>
                  {log.result.batch && (
                    <Anchor component={Link} to={`/batches/${log.result.batch.id}`} size="xs">
                      {log.result.batch.code}
                    </Anchor>
                  )}
                  {log.result.shipment && (
                    <>
                      {" "}
                      ·{" "}
                      <Anchor component={Link} to={`/shipments/${log.result.shipment.id}`} size="xs">
                        {log.result.shipment.code}
                      </Anchor>
                    </>
                  )}
                </Table.Td>
                <Table.Td>
                  <Text size="sm">{log.message}</Text>
                </Table.Td>
                <Table.Td>
                  <Text size="xs" c="dimmed">
                    {new Date(log.ts).toLocaleTimeString()}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <ActionIcon
                    size="sm"
                    variant="subtle"
                    color="red"
                    onClick={() =>
                      setLogs((prev) => prev.filter((l) => l.ts !== log.ts || l.raw !== log.raw))
                    }
                  >
                    <IconTrash size={14} />
                  </ActionIcon>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
        </Table.ScrollContainer>
        </ResponsiveTable>
      </Paper>}

      <Modal
        opened={saveOpen}
        onClose={() => setSaveOpen(false)}
        title={t("scanner.saveSessionTitle")}
        size="md"
      >
        <Stack>
          <TextInput
            label={t("scanner.sessionNameLabel")}
            description={t("scanner.sessionNameDesc")}
            value={sessionName}
            onChange={(e) => setSessionName(e.currentTarget.value)}
            data-autofocus
            maxLength={120}
          />
          <Text size="sm" c="dimmed">
            {t("scanner.scansWillBeSaved", { count: logs.length })}
          </Text>
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setSaveOpen(false)}>
              {t("common.cancel")}
            </Button>
            <Button
              onClick={handleSaveSession}
              loading={saving}
              disabled={!sessionName.trim() || logs.length === 0}
            >
              {t("common.save")}
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={sessionsOpen}
        onClose={() => setSessionsOpen(false)}
        title={t("scanner.savedSessionsTitle")}
        size="lg"
      >
        <Stack>
          <Group justify="space-between">
            <Text size="sm" c="dimmed">
              {sessionsLoading
                ? t("common.loading")
                : t("scanner.sessionsCount", { count: savedSessions.length })}
            </Text>
            <Button
              size="xs"
              variant="subtle"
              onClick={() => void refreshSavedSessions()}
              loading={sessionsLoading}
            >
              {t("scanner.refresh")}
            </Button>
          </Group>
          {savedSessions.length === 0 && !sessionsLoading && (
            <Text size="sm" c="dimmed">
              {t("scanner.noSessionsYet")}
            </Text>
          )}
          <Stack gap="xs">
            {savedSessions.map((s) => {
              const canDelete =
                user.role === "admin" ||
                (s.created_by && s.created_by === user.username);
              return (
                <Paper key={s.id} withBorder p="sm">
                  <Group justify="space-between" wrap="nowrap" align="flex-start">
                    <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
                      <Text fw={600} truncate>
                        {s.name}
                      </Text>
                      <Group gap={6}>
                        <Badge variant="light" color="blue" size="sm">
                          {t("scanner.scansBadge", { count: s.log_count })}
                        </Badge>
                        {s.mode && (
                          <Badge variant="light" color="gray" size="sm">
                            {s.mode}
                          </Badge>
                        )}
                        {s.box_number && (
                          <Badge variant="light" color="grape" size="sm">
                            {t("scanner.boxBadge", { box: s.box_number })}
                          </Badge>
                        )}
                      </Group>
                      <Text size="xs" c="dimmed">
                        {s.created_by ? `${s.created_by} · ` : ""}
                        {s.created_at
                          ? new Date(s.created_at).toLocaleString("ro-RO")
                          : ""}
                      </Text>
                    </Stack>
                    <Group gap="xs" wrap="nowrap">
                      <Button
                        size="xs"
                        variant="light"
                        onClick={() => void handleLoadSession(s.id)}
                      >
                        {t("scanner.load")}
                      </Button>
                      {canDelete && (
                        <ActionIcon
                          color="red"
                          variant="subtle"
                          onClick={() => void handleDeleteSession(s.id)}
                        >
                          <IconTrash size={14} />
                        </ActionIcon>
                      )}
                    </Group>
                  </Group>
                </Paper>
              );
            })}
          </Stack>
        </Stack>
      </Modal>
    </Container>
  );
}

function StatusBadge({ status }: { status: ScanLog["status"] }) {
  const { t } = useTranslation();
  const map = {
    ok: { color: "blue", label: t("scanner.statusInfo") },
    applied: { color: "green", label: t("scanner.statusOk") },
    warn: { color: "yellow", label: t("scanner.statusWarn") },
    err: { color: "red", label: t("scanner.statusErr") },
  } as const;
  const { color, label } = map[status];
  return (
    <Badge color={color} variant="light" size="sm">
      {label}
    </Badge>
  );
}

interface BulkPanelProps {
  entries: BulkEntry[];
  boxNumber: string;
  submitting: boolean;
  canEdit: boolean;
  lastResult: BulkAssignResult | null;
  onRemove: (ts: number) => void;
  onClear: () => void;
  onSubmit: () => void;
}

function BulkPanel({
  entries,
  boxNumber,
  submitting,
  canEdit,
  lastResult,
  onRemove,
  onClear,
  onSubmit,
}: BulkPanelProps) {
  const { t } = useTranslation();
  const counts = useMemo(() => {
    const c = { buffered: 0, applied: 0, conflict: 0, not_found: 0 };
    for (const b of entries) c[b.status]++;
    return c;
  }, [entries]);
  const total = entries.length;

  return (
    <Paper withBorder p="md" mb="md">
      <Group justify="space-between" mb="sm" align="flex-start">
        <Stack gap={2}>
          <Group gap="xs" align="center">
            <Title order={3}>
              {counts.buffered}
              <Text component="span" c="dimmed" size="md" fw={400}>
                {" "}
                {t("scanner.inBoxLabel")} <b>{boxNumber || t("common.none")}</b>
              </Text>
            </Title>
          </Group>
          <Group gap={6}>
            {counts.applied > 0 && (
              <Badge color="green" variant="light">
                {t("scanner.savedBadge", { count: counts.applied })}
              </Badge>
            )}
            {counts.conflict > 0 && (
              <Badge color="yellow" variant="light">
                {t("scanner.conflictsBadge", { count: counts.conflict })}
              </Badge>
            )}
            {counts.not_found > 0 && (
              <Badge color="red" variant="light">
                {t("scanner.missingDbBadge", { count: counts.not_found })}
              </Badge>
            )}
          </Group>
        </Stack>
        <Group>
          <Button
            variant="subtle"
            size="sm"
            disabled={total === 0}
            onClick={onClear}
          >
            {t("scanner.clearBuffer", { count: total })}
          </Button>
          <Button
            size="md"
            leftSection={<IconUpload size={16} />}
            loading={submitting}
            disabled={!canEdit || counts.buffered === 0}
            onClick={onSubmit}
          >
            {t("scanner.saveCount", { count: counts.buffered })}
          </Button>
        </Group>
      </Group>

      {lastResult && (
        <Alert
          mb="sm"
          color={lastResult.applied.length > 0 ? "green" : "yellow"}
          title={t("scanner.lastSaveInBox", { box: lastResult.box_number })}
        >
          <Text size="sm">
            {t("scanner.lastSaveAppliedLine", { count: lastResult.applied.length })}
            {lastResult.conflicts.length > 0 && t("scanner.lastSaveConflictsLine", { count: lastResult.conflicts.length })}
            {lastResult.not_found.length > 0 && t("scanner.lastSaveNotFoundLine", { count: lastResult.not_found.length })}
          </Text>
        </Alert>
      )}

      <ResponsiveTable
        stagger={false}
        mobile={{
          rows: entries,
          rowKey: (b) => b.ts + "-" + b.query,
          title: (b) => <Text ff="monospace" size="sm" span>{b.query}</Text>,
          subtitle: (b) => new Date(b.ts).toLocaleTimeString(),
          badge: (b) => (
            <Group gap={4} wrap="nowrap">
              <BulkStatusBadge status={b.status} />
              {b.status === "applied" && b.product_type_slug && (
                <Badge color={b.product_type_slug === "antenna" ? "grape" : "gray"} variant="outline" size="sm">
                  {b.product_type_slug === "antenna" ? t("antennaConfirm.typeAntenna") : t("antennaConfirm.typeModule")}
                </Badge>
              )}
            </Group>
          ),
          fields: [
            {
              label: t("scanner.resolvedDevEui"),
              render: (b) => b.resolved_dev_eui ? (
                <Text ff="monospace" size="sm" c="dimmed">{b.resolved_dev_eui.toLowerCase()}</Text>
              ) : (
                <Text size="sm" c="dimmed">{t("common.none")}</Text>
              ),
            },
            { label: t("scanner.colMessage"), render: (b) => b.message ?? "", hidden: (b) => !b.message },
          ],
          actions: (b) => (
            <ActionIcon variant="light" color="red" size="lg" onClick={() => onRemove(b.ts)}>
              <IconTrash size={18} />
            </ActionIcon>
          ),
          empty: t("scanner.bufferEmpty"),
        }}
      >
      <ScrollArea h={360} type="auto">
        <Table striped withTableBorder miw={640}>
          <Table.Thead>
            <Table.Tr>
              <Table.Th w={150}>{t("scanner.colStatus")}</Table.Th>
              <Table.Th>{t("scanner.scannedCode")}</Table.Th>
              <Table.Th>{t("scanner.resolvedDevEui")}</Table.Th>
              <Table.Th>{t("scanner.colMessage")}</Table.Th>
              <Table.Th w={80}>{t("scanner.colTime")}</Table.Th>
              <Table.Th w={50} />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {entries.length === 0 && (
              <Table.Tr>
                <Table.Td colSpan={6}>
                  <Text c="dimmed" size="sm">
                    {t("scanner.bufferEmpty")}
                  </Text>
                </Table.Td>
              </Table.Tr>
            )}
            {entries.map((b) => (
              <Table.Tr key={b.ts + "-" + b.query}>
                <Table.Td>
                  <Group gap={4} wrap="nowrap">
                    <BulkStatusBadge status={b.status} />
                    {b.status === "applied" && b.product_type_slug && (
                      <Badge
                        color={b.product_type_slug === "antenna" ? "grape" : "gray"}
                        variant="outline"
                        size="sm"
                      >
                        {b.product_type_slug === "antenna"
                          ? t("antennaConfirm.typeAntenna")
                          : t("antennaConfirm.typeModule")}
                      </Badge>
                    )}
                  </Group>
                </Table.Td>
                <Table.Td>
                  <Text ff="monospace" size="sm" truncate maw={260}>
                    {b.query}
                  </Text>
                </Table.Td>
                <Table.Td>
                  {b.resolved_dev_eui ? (
                    <Text ff="monospace" size="sm" c="dimmed">
                      {b.resolved_dev_eui.toLowerCase()}
                    </Text>
                  ) : (
                    <Text size="sm" c="dimmed">
                      {t("common.none")}
                    </Text>
                  )}
                </Table.Td>
                <Table.Td>
                  <Text size="sm">{b.message ?? ""}</Text>
                </Table.Td>
                <Table.Td>
                  <Text size="xs" c="dimmed">
                    {new Date(b.ts).toLocaleTimeString()}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <ActionIcon
                    variant="subtle"
                    color="red"
                    size="sm"
                    onClick={() => onRemove(b.ts)}
                  >
                    <IconTrash size={14} />
                  </ActionIcon>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </ScrollArea>
      </ResponsiveTable>
    </Paper>
  );
}

function BulkStatusBadge({ status }: { status: BulkEntry["status"] }) {
  const { t } = useTranslation();
  const map = {
    buffered: { color: "blue", label: t("scanner.bulkStatusBuffer") },
    applied: { color: "green", label: t("scanner.statusOk") },
    conflict: { color: "yellow", label: t("scanner.bulkStatusConflict") },
    not_found: { color: "red", label: t("scanner.bulkStatusMissing") },
  } as const;
  const { color, label } = map[status];
  return (
    <Badge color={color} variant="light" size="sm">
      {label}
    </Badge>
  );
}
